import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

interface SlideToolbarPopoverGroupContextValue {
  /** Which of the toolbar's popovers is open, if any. */
  openPopoverId: string | null;
  /** Open this popover, closing whichever one was open. */
  openPopover: (id: string) => void;
  /** Close this popover if it's the open one. */
  closePopover: (id: string) => void;
  /**
   * Close whatever is open. Used by the plain toolbar buttons so acting on one
   * of them also collapses a sibling's open menu.
   */
  closeAllPopovers: () => void;
}

const SlideToolbarPopoverGroupContext =
  createContext<SlideToolbarPopoverGroupContextValue | null>(null);

/**
 * Returns the toolbar's popover coordination, or null when there's no group —
 * a `SlideToolbarButtonWithPopover` rendered outside a `SlideToolbar` keeps
 * managing its own open state.
 */
export function useSlideToolbarPopoverGroup(): SlideToolbarPopoverGroupContextValue | null {
  return useContext(SlideToolbarPopoverGroupContext);
}

/**
 * Keeps a slide toolbar to one open popover at a time: clicking a toolbar
 * button collapses whichever menu was open.
 *
 * This has to be coordinated explicitly because a menu's own backdrop can't do
 * it. The hover toolbars render in a Popper on the POPOVER layer while MUI
 * menus render on the (lower) MODAL layer, so the toolbar's buttons sit *above*
 * an open menu's invisible backdrop — a click on a second button reaches that
 * button instead of dismissing the first menu, leaving both menus open.
 */
export function SlideToolbarPopoverGroupProvider({
  children,
}: PropsWithChildren) {
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  // Mirrors state so `closePopover` can tell a stale close from a live one
  // without waiting for a re-render.
  const openPopoverIdRef = useRef<string | null>(null);

  const setOpen = useCallback((id: string | null) => {
    openPopoverIdRef.current = id;
    setOpenPopoverId(id);
  }, []);

  const openPopover = useCallback(
    (id: string) => {
      setOpen(id);
    },
    [setOpen]
  );

  const closePopover = useCallback(
    (id: string) => {
      // Ignore a close from a popover that isn't the open one — the popover
      // just switched away from reports its own close after the new one has
      // opened, which would otherwise clear the new popover straight away.
      if (openPopoverIdRef.current !== id) return;
      setOpen(null);
    },
    [setOpen]
  );

  const closeAllPopovers = useCallback(() => {
    if (openPopoverIdRef.current === null) return;
    setOpen(null);
  }, [setOpen]);

  const value = useMemo<SlideToolbarPopoverGroupContextValue>(
    () => ({
      openPopoverId,
      openPopover,
      closePopover,
      closeAllPopovers,
    }),
    [openPopoverId, openPopover, closePopover, closeAllPopovers]
  );

  return (
    <SlideToolbarPopoverGroupContext.Provider value={value}>
      {children}
    </SlideToolbarPopoverGroupContext.Provider>
  );
}
